import React, { useState, useMemo, useCallback, memo, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { issueCard, updateCardEmail, updateCardPhone } from '../api/CardAPI';
import WalletsAPI from '../api/WalletsAPI';
import { BIN_PRODUCT_MAPPING } from '../config/cardBinConfig';
import { db } from '../firebase';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import cardPricingManager from '../utils/cardPricingManager';
import feeManager from '../utils/feeManager';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  InputAdornment,
  IconButton,
  Fade,
  Zoom,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  RadioGroup,
  Radio,
  FormLabel,
  FormControlLabel,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowBack,
  CreditCard,
  LocationOn,
  AttachMoney,
  Check,
  AddCircleOutline,
  AccountBalanceWallet,
  Verified,
  Apple,
  Flag,
  Close,
  ShoppingCart,
  Phone,
  Email,
} from '@mui/icons-material';

// Memoized Card Component
const VirtualCardItem = memo(({ bin, config, index, selectedBin, onSelect, getCardGradient, getCountryFlag, dynamicPrice }) => {
  return (
    <Grid item xs={12} md={6} lg={4}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.05 }}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <Card
          sx={{
            cursor: 'pointer',
            height: '100%',
            border: '1px solid #e2e8f0',
            transition: 'all 0.3s ease',
            overflow: 'hidden',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&:hover': {
              boxShadow: '0 20px 40px rgba(255, 214, 0, 0.15)',
              transform: 'translateY(-4px)',
              borderColor: '#FFD600',
              border: '2px solid #FFD600'
            },
            ...(selectedBin === bin && {
              border: '3px solid #FFD600',
              boxShadow: '0 10px 30px rgba(255, 214, 0, 0.3)'
            }),
            willChange: 'transform, box-shadow',
          }}
          onClick={() => onSelect(bin)}
        >
          {/* Credit Card Visual */}
          <Box
            sx={{
              background: getCardGradient(config.region),
              p: 3,
              position: 'relative',
              height: 220,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              color: 'white',
            }}
          >
            {/* Card Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h4">{getCountryFlag(config.region)}</Typography>
                {/* EMV Chip */}
                <Box
                  sx={{
                    width: 45,
                    height: 35,
                    background: 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)',
                    borderRadius: 1,
                    position: 'relative',
                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.2)',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: '20%',
                      left: '20%',
                      right: '20%',
                      bottom: '20%',
                      border: '2px solid rgba(0,0,0,0.2)',
                      borderRadius: 0.5,
                    }
                  }}
                />
              </Box>
              {/* Platform Logos on Card */}
              {config.platforms && (
                <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', maxWidth: 150 }}>
                  {config.platforms.includes('Facebook') && (
                    <Box sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      bgcolor: '#1877f2',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}>
                      <Typography sx={{ color: 'white', fontWeight: 900, fontSize: 14, fontFamily: 'sans-serif' }}>f</Typography>
                    </Box>
                  )}
                  {config.platforms.includes('Instagram') && (
                    <Box sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '20%',
                      background: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      border: '2px solid white'
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                    </Box>
                  )}
                  {config.platforms.includes('TikTok') && (
                    <Box sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '20%',
                      bgcolor: '#000',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      border: '1.5px solid white',
                      position: 'relative'
                    }}>
                      <svg width="12" height="14" viewBox="0 0 448 512" fill="white">
                        <path d="M448,209.91a210.06,210.06,0,0,1-122.77-39.25V349.38A162.55,162.55,0,1,1,185,188.31V278.2a74.62,74.62,0,1,0,52.23,71.18V0l88,0a121.18,121.18,0,0,0,1.86,22.17h0A122.18,122.18,0,0,0,381,102.39a121.43,121.43,0,0,0,67,20.14Z"/>
                      </svg>
                    </Box>
                  )}
                  {config.platforms.includes('Google Ads') && (
                    <Box sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      bgcolor: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    </Box>
                  )}
                  {config.platforms.includes('Taboola') && (
                    <Box sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '20%',
                      bgcolor: '#0c61fe',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}>
                      <Typography sx={{ color: 'white', fontWeight: 900, fontSize: 12, fontFamily: 'Arial, sans-serif' }}>t</Typography>
                    </Box>
                  )}
                </Box>
              )}
              {/* Apple Pay & Google Pay Icons */}
              {config.features && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {config.features.includes('Apple Pay') && (
                    <Box
                      sx={{
                        bgcolor: 'white',
                        borderRadius: 1.5,
                        px: 1.5,
                        py: 0.8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <Apple sx={{ color: 'black', fontSize: 18 }} />
                      <Typography sx={{ color: 'black', fontSize: 11, fontWeight: 600 }}>
                        Pay
                      </Typography>
                    </Box>
                  )}
                  {config.features.includes('Google Pay') && (
                    <Box
                      sx={{
                        bgcolor: 'white',
                        borderRadius: 1.5,
                        px: 1.5,
                        py: 0.8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0z" fill="#4285F4"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                        <path d="M5.84 9.91C5.56 10.57 5.38 11.27 5.38 12c0 .73.18 1.43.46 2.09l-3.66 2.84C1.43 15.43.62 13.79.62 12c0-1.79.81-3.43 2.56-4.93l3.66 2.84z" fill="#FBBC05"/>
                        <path d="M12 18.62c-2.86 0-5.29-1.93-6.16-4.53L2.18 16.93C3.99 20.53 7.7 23 12 23c2.97 0 5.45-1.09 7.36-2.87l-3.6-2.8C14.56 18.11 13.35 18.62 12 18.62z" fill="#34A853"/>
                        <path d="M23 12c0-.83-.08-1.53-.23-2.25H12v4.5h6.19c-.27 1.43-1.08 2.64-2.31 3.45l3.6 2.8C21.45 18.55 23 15.47 23 12z" fill="#4285F4"/>
                      </svg>
                      <Typography sx={{ color: 'black', fontSize: 11, fontWeight: 600 }}>
                        Pay
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            {/* Card Number */}
            <Box>
              <Typography
                sx={{
                  fontFamily: 'monospace',
                  fontSize: 20,
                  letterSpacing: 3,
                  mb: 3,
                  color: 'white',
                }}
              >
                {bin.substring(0, 4)} •••• •••• ••••
              </Typography>

              {/* Card Footer */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <Box>
                  <Typography sx={{ fontSize: 10, opacity: 0.7, mb: 0.5, color: 'white' }}>CARD HOLDER</Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'white' }}>YOUR NAME HERE</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 10, opacity: 0.7, mb: 0.5, color: 'white' }}>EXPIRES</Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'white' }}>MM/YY</Typography>
                </Box>
                {/* Mastercard Logo */}
                <Box sx={{ display: 'flex', position: 'relative' }}>
                  <Box
                    sx={{
                      width: 35,
                      height: 35,
                      borderRadius: '50%',
                      bgcolor: '#eb001b',
                      opacity: 0.9,
                    }}
                  />
                  <Box
                    sx={{
                      width: 35,
                      height: 35,
                      borderRadius: '50%',
                      bgcolor: '#f79e1b',
                      opacity: 0.9,
                      ml: -2,
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Card Details */}
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#1e293b' }}>
              {config.description}
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
              {config.region} • Virtual Mastercard
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              <Chip
                label={`$${dynamicPrice || config.price || 10} Issue Fee`}
                size="small"
                sx={{ bgcolor: '#f1f5f9', color: '#475569', fontWeight: 600 }}
              />
              <Chip
                label={config.currency}
                size="small"
                sx={{ bgcolor: '#FFD600', color: '#1a1a1a', fontWeight: 600 }}
              />
              {config.features?.map(feature => (
                <Chip
                  key={feature}
                  label={feature}
                  size="small"
                  sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 600 }}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      </motion.div>
    </Grid>
  );
});

// Memoized Premium Card Component
const PremiumCardItem = memo(({ bin, config, selectedBin, onSelect, getCardGradient, getCountryFlag, dynamicPrice }) => {
  return (
    <Grid item xs={12} md={6} lg={4}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <Card
          sx={{
            cursor: 'pointer',
            height: '100%',
            border: selectedBin === bin ? '3px solid #FFD600' : '1px solid #e2e8f0',
            transition: 'all 0.3s ease',
            overflow: 'hidden',
            boxShadow: selectedBin === bin ? '0 10px 30px rgba(255, 214, 0, 0.2)' : '0 2px 12px rgba(0,0,0,0.08)',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.9) 100%)',
            '&:hover': {
              boxShadow: '0 20px 40px rgba(255, 214, 0, 0.15)',
              transform: 'translateY(-4px)',
              borderColor: '#FFC400'
            },
            willChange: 'transform, box-shadow',
          }}
          onClick={() => onSelect(bin)}
        >
          {/* Credit Card Visual */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
              p: 3,
              position: 'relative',
              height: 220,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              color: 'white !important',
              '& *': {
                color: 'white !important'
              }
            }}
          >
            {/* Card Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h4">{getCountryFlag(config.region)}</Typography>
                {/* EMV Chip */}
                <Box
                  sx={{
                    width: 45,
                    height: 35,
                    background: 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)',
                    borderRadius: 1,
                    position: 'relative',
                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.2)',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: '20%',
                      left: '20%',
                      right: '20%',
                      bottom: '20%',
                      border: '2px solid rgba(0,0,0,0.2)',
                      borderRadius: 0.5,
                    }
                  }}
                />
              </Box>
              {config.features && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {config.features.includes('Apple Pay') && (
                    <Box
                      sx={{
                        bgcolor: 'white',
                        borderRadius: 1.5,
                        px: 1.5,
                        py: 0.8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        isolation: 'isolate',
                        '& *': {
                          color: 'black !important'
                        },
                        '& svg': {
                          color: 'black !important',
                          fill: 'black !important'
                        }
                      }}
                    >
                      <Apple sx={{ color: 'black !important', fontSize: 18, fill: 'black !important' }} />
                      <Typography sx={{ color: 'black !important', fontSize: 11, fontWeight: 600 }}>
                        Pay
                      </Typography>
                    </Box>
                  )}
                  {config.features.includes('Google Pay') && (
                    <Box
                      sx={{
                        bgcolor: 'white',
                        borderRadius: 1.5,
                        px: 1.5,
                        py: 0.8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        isolation: 'isolate'
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0z" fill="#4285F4"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                        <path d="M5.84 9.91C5.56 10.57 5.38 11.27 5.38 12c0 .73.18 1.43.46 2.09l-3.66 2.84C1.43 15.43.62 13.79.62 12c0-1.79.81-3.43 2.56-4.93l3.66 2.84z" fill="#FBBC05"/>
                        <path d="M12 18.62c-2.86 0-5.29-1.93-6.16-4.53L2.18 16.93C3.99 20.53 7.7 23 12 23c2.97 0 5.45-1.09 7.36-2.87l-3.6-2.8C14.56 18.11 13.35 18.62 12 18.62z" fill="#34A853"/>
                        <path d="M23 12c0-.83-.08-1.53-.23-2.25H12v4.5h6.19c-.27 1.43-1.08 2.64-2.31 3.45l3.6 2.8C21.45 18.55 23 15.47 23 12z" fill="#4285F4"/>
                      </svg>
                      <Typography sx={{ color: 'black !important', fontSize: 11, fontWeight: 600 }}>
                        Pay
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            {/* Card Number */}
            <Box>
              <Typography
                sx={{
                  fontFamily: 'monospace',
                  fontSize: 20,
                  letterSpacing: 3,
                  mb: 3,
                  color: 'white !important'
                }}
              >
                {bin.substring(0, 4)} •••• •••• ••••
              </Typography>

              {/* Card Footer */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <Box>
                  <Typography sx={{ fontSize: 10, opacity: 0.7, mb: 0.5, color: 'rgba(255, 255, 255, 0.7) !important' }}>CARD HOLDER</Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'white !important' }}>YOUR NAME HERE</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 10, opacity: 0.7, mb: 0.5, color: 'rgba(255, 255, 255, 0.7) !important' }}>EXPIRES</Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'white !important' }}>MM/YY</Typography>
                </Box>
                {/* Mastercard Logo */}
                <Box sx={{ display: 'flex', position: 'relative' }}>
                  <Box
                    sx={{
                      width: 35,
                      height: 35,
                      borderRadius: '50%',
                      bgcolor: '#eb001b',
                      opacity: 0.9,
                    }}
                  />
                  <Box
                    sx={{
                      width: 35,
                      height: 35,
                      borderRadius: '50%',
                      bgcolor: '#f79e1b',
                      opacity: 0.9,
                      ml: -2,
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Card Details */}
          <CardContent sx={{ p: 3, bgcolor: 'white' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#1e293b' }}>
              {config.description}
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
              {config.region} • Virtual Mastercard
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={`$${dynamicPrice || config.price || 10} Issue Fee`}
                size="small"
                sx={{ bgcolor: '#1a1a1a', color: 'white' }}
              />
              <Chip 
                label={config.currency} 
                size="small" 
                color="primary"
              />
              {config.features?.map(feature => (
                <Chip
                  key={feature}
                  label={feature}
                  size="small"
                  sx={{ bgcolor: '#000', color: 'white' }}
                  icon={feature === 'Apple Pay' ?
                    <Apple sx={{ fontSize: 16, color: 'white' }} /> :
                    feature === 'Google Pay' ? (
                      <svg width="16" height="16" viewBox="0 0 24 24">
                        <path fill="white" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="white" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="white" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="white" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    ) : null
                  }
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      </motion.div>
    </Grid>
  );
});

export default function AddCard() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [selectedBin, setSelectedBin] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedNetwork, setSelectedNetwork] = useState('tron');
  const [userBalance, setUserBalance] = useState({ usdt_tron: 0, usdt_eth: 0 });
  
  const [formData, setFormData] = useState({
    cardName: '',
    bin: '',
    initialAmount: 25,
    currency: 'USD',
    phone: '',
    email: '',
  });

  // Calculate loading fee based on amount using feeManager (with customer-specific fees)
  const calculateLoadingFee = useCallback((amount) => {
    const depositAmount = parseFloat(amount) || 0;

    if (depositAmount === 0) {
      return { feeAmount: 0, feePercentage: 0 };
    }

    // Use feeManager to get customer-specific or default fees
    // Note: feeManager.calculateFee expects (amount, customerEmail)
    const feeInfo = feeManager.calculateFee(depositAmount, currentUser?.email);

    return {
      feeAmount: parseFloat(feeInfo.feeAmount.toFixed(2)),
      feePercentage: parseFloat(feeInfo.feePercentage.toFixed(2))
    };
  }, [currentUser]);

  const handleSelectCard = useCallback((bin) => {
    setSelectedBin(bin);
    setFormData(prev => ({ ...prev, bin }));
    setError('');
    setOpenDialog(true);
  }, []);

  // Get card price from Firebase or fallback to BIN config
  const getCardPrice = useCallback((bin) => {
    const pricingInfo = cardPricingManager.getCardPrice(currentUser?.email, bin);
    const binConfig = BIN_PRODUCT_MAPPING[bin];
    return pricingInfo?.price || binConfig?.price || 10;
  }, [currentUser]);

  // Initialize card pricing manager and fee manager
  useEffect(() => {
    cardPricingManager.initialize();
    feeManager.initialize();
  }, []);

  // Fetch user balance
  useEffect(() => {
    const fetchBalance = async () => {
      if (currentUser?.email) {
        try {
          const walletStatus = await WalletsAPI.getUserWalletStatus(currentUser.email);
          if (walletStatus?.balance) {
            setUserBalance({
              usdt_tron: parseFloat(walletStatus.balance.usdt_tron) || 0,
              usdt_eth: parseFloat(walletStatus.balance.usdt_eth) || 0
            });
          }
        } catch (err) {
          console.error('Failed to fetch balance:', err);
        }
      }
    };

    fetchBalance();
  }, [currentUser]);

  const handleCreateCard = useCallback(async () => {
    if (!formData.bin) {
      setError('Please select a card type');
      return;
    }

    const binConfig = BIN_PRODUCT_MAPPING[formData.bin];
    if (!binConfig) {
      setError('Invalid BIN selected');
      return;
    }

    // Check minimum initial amount
    if (formData.initialAmount < 25) {
      setError('Minimum initial balance is $25');
      return;
    }

    // Check if premium card requires phone and email
    if (binConfig.features?.includes('Apple Pay') || binConfig.features?.includes('Google Pay')) {
      if (!formData.phone || !formData.email) {
        setError('Phone number and email are required for premium cards');
        return;
      }
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setError('Please enter a valid email address');
        return;
      }
    }

    setLoading(true);
    setError('');
    
    try {

      // Save user email to localStorage for the API
      if (currentUser?.email) {
        localStorage.setItem('userEmail', currentUser.email);
      }

      // Calculate total amount including loading fee AND card price
      const loadingFee = calculateLoadingFee(formData.initialAmount);

      // Get card price from Firebase (with customer-specific pricing support)
      const cardPrice = getCardPrice(formData.bin);

      const totalAmount = cardPrice + formData.initialAmount + loadingFee.feeAmount;
      
      // Check if user has sufficient balance in selected network
      const selectedBalance = selectedNetwork === 'ethereum' ? userBalance.usdt_eth : userBalance.usdt_tron;
      if (selectedBalance < totalAmount) {
        setError(`Insufficient ${selectedNetwork.toUpperCase()} balance. You have $${selectedBalance.toFixed(2)} but need $${totalAmount.toFixed(2)} (Card: $${cardPrice} + Initial: $${formData.initialAmount} + Fee: $${loadingFee.feeAmount.toFixed(2)})`);
        return;
      }
      
      // Issue the card - create the card first
      const result = await issueCard(
        binConfig.productCode,
        formData.bin,
        formData.initialAmount,
        formData.currency,
        formData.cardName || `${binConfig.description} Card`,
        null,
        null,
        selectedNetwork,
        cardPrice,  // Pass card price
        loadingFee.feeAmount,  // Pass fee amount
        totalAmount  // Pass total amount
      );

      console.log('Card created:', result);

      // Card created successfully, now handle topup if needed
      if (result.san) {
        // For Apple Pay/Google Pay cards, update email and phone
        if ((binConfig.features?.includes('Apple Pay') || binConfig.features?.includes('Google Pay'))) {
          if (formData.email && formData.phone) {
            try {
              await updateCardEmail(result.san, formData.email);

              const phoneMatch = formData.phone.match(/^\+?(\d{1,3})\s?(.+)$/);
              if (phoneMatch) {
                const zoneNumber = phoneMatch[1];
                const phoneNumber = phoneMatch[2].replace(/\s|-/g, '');
                await updateCardPhone(result.san, zoneNumber, phoneNumber);
              }
            } catch (updateError) {
              console.error('Error updating card details:', updateError);
            }
          }
        }

        // TODO: Topup the card with initial amount if needed
        // This will be handled after we update the topup endpoint

        // Save card info to user's profile in Firebase
        await updateDoc(doc(db, 'users', currentUser.uid), {
          cards: arrayUnion({
            san: result.san,
            cardName: formData.cardName || `${binConfig.description} Card`,
            bin: formData.bin,
            productCode: binConfig.productCode,
            region: binConfig.region,
            createdAt: new Date().toISOString(),
            status: 'active',
            balance: result.balance || 0,
            currency: result.currency || 'USD'
          })
        });

        setSuccess(true);
        setTimeout(() => {
          navigate('/customer');
        }, 2000);
      } else {
        throw new Error('Card creation failed - no SAN returned');
      }
    } catch (err) {
      console.error('Error creating card:', err);
      setError(err.message || 'Failed to create card');
    } finally {
      setLoading(false);
    }
  }, [formData, currentUser, navigate, selectedNetwork, userBalance]);

  const getCardGradient = useCallback((region) => {
    switch(region) {
      case 'Hong Kong':
        return 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)';
      case 'United Kingdom':
        return 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)';
      default:
        return 'linear-gradient(135deg, #2563eb 0%, #60a5fa 100%)';
    }
  }, []);

  const getCountryFlag = useCallback((region) => {
    switch(region) {
      case 'Hong Kong':
        return '🇭🇰';
      case 'United Kingdom':
        return '🇬🇧';
      default:
        return '🌍';
    }
  }, []);

  // Memoize card lists
  const hongKongCards = useMemo(() => 
    Object.entries(BIN_PRODUCT_MAPPING)
      .filter(([bin, config]) => config.region === 'Hong Kong' && !config.features?.includes('Apple Pay')),
    []
  );

  const ukCards = useMemo(() => 
    Object.entries(BIN_PRODUCT_MAPPING)
      .filter(([bin, config]) => config.region === 'United Kingdom'),
    []
  );

  const digitalWalletCards = useMemo(() =>
    Object.entries(BIN_PRODUCT_MAPPING)
      .filter(([bin, config]) => config.features?.includes('Apple Pay') || config.features?.includes('Google Pay')),
    []
  );

  const handleFormChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  if (success) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Paper
            sx={{
              p: 8,
              textAlign: 'center',
              background: 'linear-gradient(135deg, #FFD600 0%, #FFC400 100%)',
              color: 'white',
              borderRadius: 4,
              boxShadow: '0 20px 60px rgba(255, 214, 0, 0.3)'
            }}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            >
              <Check
                sx={{
                  fontSize: 100,
                  bgcolor: 'white',
                  color: '#FFD600',
                  borderRadius: '50%',
                  p: 2,
                  mb: 4,
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
                }}
              />
            </motion.div>
            <Typography variant="h3" sx={{ mb: 2, fontWeight: 700 }}>
              Card Created Successfully!
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9 }}>
              Redirecting to your dashboard...
            </Typography>
          </Paper>
        </motion.div>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', pt: 8, pb: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ mb: 4, pt: 3 }}>
            <Link to="/customer" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                startIcon={<ArrowBack />}
                sx={{
                  mb: 3,
                  bgcolor: '#FFD600',
                  color: 'white',
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: 16,
                  padding: '10px 24px',
                  boxShadow: '0 4px 12px rgba(255, 214, 0, 0.3)',
                  '&:hover': {
                    bgcolor: '#FFAA00',
                    transform: 'translateX(-4px)',
                    boxShadow: '0 6px 16px rgba(255, 214, 0, 0.4)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Back to Dashboard
              </Button>
            </Link>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #FFD600 0%, #FFC400 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 10px 30px rgba(255, 214, 0, 0.3)'
                }}
              >
                <CreditCard sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 800, color: '#1e293b' }}>
                  Create New Card
                </Typography>
                <Typography variant="h6" sx={{ color: '#64748b', fontWeight: 400 }}>
                  Choose your virtual card and start spending securely
                </Typography>
              </Box>
            </Box>
          </Box>
        </motion.div>

        {error && (
          <Fade in={true}>
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          </Fade>
        )}

        {/* Apple Pay Card - Special Section */}
        <Box sx={{ mb: 6, p: 4, bgcolor: '#f1f5f9', borderRadius: 3 }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
            Premium Cards 
            <Apple sx={{ fontSize: 28 }} />
            <svg width="28" height="28" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          </Typography>
          
          <Grid container spacing={3}>
            {digitalWalletCards.map(([bin, config]) => (
              <PremiumCardItem
                key={bin}
                bin={bin}
                config={config}
                selectedBin={selectedBin}
                onSelect={handleSelectCard}
                getCardGradient={getCardGradient}
                getCountryFlag={getCountryFlag}
                dynamicPrice={getCardPrice(bin)}
              />
            ))}

            {/* Old Google Pay Card Placeholder - Remove this entire section */}
            {false && (
            <Grid item xs={12} md={6} lg={4}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                whileHover={{ scale: 1.02 }}
              >
                <Card
                  sx={{
                    height: '100%',
                    border: '1px solid #e2e8f0',
                    transition: 'all 0.3s ease',
                    overflow: 'hidden',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.9) 100%)',
                    position: 'relative',
                    cursor: 'not-allowed',
                    opacity: 0.7,
                    '&:hover': {
                      boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  {/* Coming Soon Badge */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      bgcolor: '#4285F4',
                      color: 'white',
                      px: 3,
                      py: 1.5,
                      borderRadius: 25,
                      fontSize: 14,
                      fontWeight: 700,
                      zIndex: 10,
                      boxShadow: '0 4px 20px rgba(66, 133, 244, 0.5)',
                    }}
                  >
                    Coming Soon
                  </Box>

                  {/* Credit Card Visual */}
                  <Box
                    sx={{
                      background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
                      p: 3,
                      position: 'relative',
                      height: 220,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      color: 'white !important',
                      filter: 'grayscale(0.3)',
                      '& *': {
                        color: 'white !important'
                      }
                    }}
                  >
                    {/* Card Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h4">🌍</Typography>
                        {/* EMV Chip */}
                        <Box
                          sx={{
                            width: 45,
                            height: 35,
                            background: 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)',
                            borderRadius: 1,
                            position: 'relative',
                            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.2)',
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              top: '20%',
                              left: '20%',
                              right: '20%',
                              bottom: '20%',
                              border: '2px solid rgba(0,0,0,0.2)',
                              borderRadius: 0.5,
                            }
                          }}
                        />
                      </Box>
                      <Box
                        sx={{
                          bgcolor: 'white',
                          borderRadius: 1.5,
                          p: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          isolation: 'isolate',
                          '& *': {
                            color: 'black !important'
                          }
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        <Typography sx={{ color: 'black !important', fontSize: 12, fontWeight: 600 }}>
                          Pay
                        </Typography>
                      </Box>
                    </Box>

                    {/* Card Number */}
                    <Box>
                      <Typography
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: 20,
                          letterSpacing: 3,
                          mb: 3,
                          color: 'white !important'
                        }}
                      >
                        •••• •••• •••• ••••
                      </Typography>

                      {/* Card Footer */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                        <Box>
                          <Typography sx={{ fontSize: 10, opacity: 0.7, mb: 0.5, color: 'rgba(255, 255, 255, 0.7) !important' }}>CARD HOLDER</Typography>
                          <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'white !important' }}>YOUR NAME HERE</Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: 10, opacity: 0.7, mb: 0.5, color: 'rgba(255, 255, 255, 0.7) !important' }}>EXPIRES</Typography>
                          <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'white !important' }}>MM/YY</Typography>
                        </Box>
                        {/* Mastercard Logo */}
                        <Box sx={{ display: 'flex', position: 'relative' }}>
                          <Box
                            sx={{
                              width: 35,
                              height: 35,
                              borderRadius: '50%',
                              bgcolor: '#eb001b',
                              opacity: 0.9,
                            }}
                          />
                          <Box
                            sx={{
                              width: 35,
                              height: 35,
                              borderRadius: '50%',
                              bgcolor: '#f79e1b',
                              opacity: 0.9,
                              ml: -2,
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {/* Card Details */}
                  <CardContent sx={{ p: 3, bgcolor: 'white' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#1e293b' }}>
                      Google Pay Virtual Card
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                      Global • Virtual Mastercard
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip 
                        label="$15 Issue Fee" 
                        size="small" 
                        sx={{ bgcolor: '#1a1a1a', color: 'white' }}
                      />
                      <Chip 
                        label="Multi-Currency" 
                        size="small" 
                        color="primary"
                      />
                      <Chip 
                        label="Google Pay" 
                        size="small" 
                        sx={{ bgcolor: '#4285F4', color: 'white' }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
            )}
          </Grid>
        </Box>

        {/* Advertising Cards Section */}
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: '#1e293b' }}>
          Advertising & Marketing Cards 📢
        </Typography>
        
        <Grid container spacing={3} sx={{ mb: 6 }}>
          {hongKongCards.map(([bin, config], index) => (
            <VirtualCardItem
              key={bin}
              bin={bin}
              config={config}
              index={index}
              selectedBin={selectedBin}
              onSelect={handleSelectCard}
              getCardGradient={getCardGradient}
              getCountryFlag={getCountryFlag}
              dynamicPrice={getCardPrice(bin)}
            />
          ))}
          {ukCards.map(([bin, config], index) => (
            <VirtualCardItem
              key={bin}
              bin={bin}
              config={config}
              index={index + hongKongCards.length}
              selectedBin={selectedBin}
              onSelect={handleSelectCard}
              getCardGradient={getCardGradient}
              getCountryFlag={getCountryFlag}
              dynamicPrice={getCardPrice(bin)}
            />
          ))}
        </Grid>

        {/* Card Configuration Dialog */}
        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="md"
          fullWidth
          TransitionComponent={Zoom}
          TransitionProps={{ timeout: 400 }}
          PaperProps={{
            sx: {
              borderRadius: 4,
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            }
          }}
        >
          {selectedBin && (
            <>
              {/* Dialog Header with Card Preview */}
              <Box
                sx={{
                  background: getCardGradient(BIN_PRODUCT_MAPPING[selectedBin]?.region),
                  p: 3,
                  position: 'relative',
                  color: 'white',
                }}
              >
                <IconButton
                  onClick={() => setOpenDialog(false)}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: 'white',
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                    }
                  }}
                >
                  <Close />
                </IconButton>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Typography variant="h4">
                    {getCountryFlag(BIN_PRODUCT_MAPPING[selectedBin]?.region)}
                  </Typography>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
                      {BIN_PRODUCT_MAPPING[selectedBin]?.description}
                    </Typography>
                    <Typography sx={{ opacity: 0.9, color: 'white' }}>
                      {BIN_PRODUCT_MAPPING[selectedBin]?.region} • Virtual Mastercard
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={`$${getCardPrice(selectedBin)} Issue Fee`}
                    size="small"
                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white', backdropFilter: 'blur(10px)' }}
                  />
                  <Chip
                    label={BIN_PRODUCT_MAPPING[selectedBin]?.currency}
                    size="small"
                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white', backdropFilter: 'blur(10px)' }}
                  />
                  {BIN_PRODUCT_MAPPING[selectedBin]?.features?.map(feature => (
                    <Chip 
                      key={feature} 
                      label={feature} 
                      size="small" 
                      sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white', backdropFilter: 'blur(10px)' }}
                    />
                  ))}
                </Box>
              </Box>

              <DialogContent sx={{ p: 4 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#1e293b' }}>
                  Configure Your Card
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Card Name (Optional)"
                      value={formData.cardName}
                      onChange={(e) => handleFormChange('cardName', e.target.value)}
                      placeholder="e.g., Shopping Card, Travel Card"
                      helperText="Give your card a memorable name"
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: '#FFD600',
                          },
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Initial Balance"
                      value={formData.initialAmount}
                      onChange={(e) => handleFormChange('initialAmount', parseFloat(e.target.value) || 0)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        inputProps: { min: 25 }
                      }}
                      helperText="Initial amount to load on the card (minimum $25)"
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: '#FFD600',
                          },
                        }
                      }}
                    />
                  </Grid>

                  {/* Network Selection */}
                  <Grid item xs={12}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
                        Select Payment Network
                      </FormLabel>
                      <RadioGroup
                        value={selectedNetwork}
                        onChange={(e) => setSelectedNetwork(e.target.value)}
                        row
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 2,
                                cursor: 'pointer',
                                border: selectedNetwork === 'tron' ? '2px solid #FFD600' : '1px solid #e0e0e0',
                                bgcolor: selectedNetwork === 'tron' ? 'rgba(255, 214, 0, 0.05)' : 'transparent',
                                transition: 'all 0.3s',
                                '&:hover': {
                                  borderColor: '#FFD600',
                                  bgcolor: 'rgba(255, 214, 0, 0.02)'
                                }
                              }}
                            >
                              <FormControlLabel
                                value="tron"
                                control={<Radio />}
                                label={
                                  <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                      TRON Network
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Available: ${userBalance.usdt_tron.toFixed(2)} USDT
                                    </Typography>
                                    {userBalance.usdt_tron < (getCardPrice(selectedBin) + calculateLoadingFee(formData.initialAmount).totalAmount) && (
                                      <Typography variant="caption" color="error">
                                        Insufficient balance
                                      </Typography>
                                    )}
                                  </Box>
                                }
                                sx={{ width: '100%', m: 0 }}
                              />
                            </Paper>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 2,
                                cursor: 'pointer',
                                border: selectedNetwork === 'ethereum' ? '2px solid #FFD600' : '1px solid #e0e0e0',
                                bgcolor: selectedNetwork === 'ethereum' ? 'rgba(255, 214, 0, 0.05)' : 'transparent',
                                transition: 'all 0.3s',
                                '&:hover': {
                                  borderColor: '#FFD600',
                                  bgcolor: 'rgba(255, 214, 0, 0.02)'
                                }
                              }}
                            >
                              <FormControlLabel
                                value="ethereum"
                                control={<Radio />}
                                label={
                                  <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                      Ethereum Network
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Available: ${userBalance.usdt_eth.toFixed(2)} USDT
                                    </Typography>
                                    {userBalance.usdt_eth < (getCardPrice(selectedBin) + calculateLoadingFee(formData.initialAmount).totalAmount) && (
                                      <Typography variant="caption" color="error">
                                        Insufficient balance
                                      </Typography>
                                    )}
                                  </Box>
                                }
                                sx={{ width: '100%', m: 0 }}
                              />
                            </Paper>
                          </Grid>
                        </Grid>
                      </RadioGroup>
                    </FormControl>
                  </Grid>

                  {/* Phone and Email fields for Premium cards only */}
                  {(BIN_PRODUCT_MAPPING[selectedBin]?.features?.includes('Apple Pay') ||
                    BIN_PRODUCT_MAPPING[selectedBin]?.features?.includes('Google Pay')) && (
                    <>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Phone Number"
                          value={formData.phone}
                          onChange={(e) => handleFormChange('phone', e.target.value)}
                          placeholder="+1 234 567 8900"
                          helperText="Required for premium card verification"
                          variant="outlined"
                          required
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Phone sx={{ color: '#64748b' }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '&:hover fieldset': {
                                borderColor: '#FFD600',
                              },
                            }
                          }}
                        />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          type="email"
                          label="Email Address"
                          value={formData.email}
                          onChange={(e) => handleFormChange('email', e.target.value)}
                          placeholder="your@email.com"
                          helperText="Required for premium card activation"
                          variant="outlined"
                          required
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Email sx={{ color: '#64748b' }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '&:hover fieldset': {
                                borderColor: '#FFD600',
                              },
                            }
                          }}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>

                {/* Cost Breakdown */}
                <Paper
                  elevation={0}
                  sx={{
                    mt: 3,
                    p: 3,
                    borderRadius: 3,
                    bgcolor: '#f8fafc',
                    border: '2px solid #e2e8f0',
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#1e293b' }}>
                    <ShoppingCart sx={{ mr: 1, verticalAlign: 'middle', color: '#FFD600' }} />
                    Order Summary
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography sx={{ color: '#64748b' }}>Card Issue Fee</Typography>
                    <Typography sx={{ fontWeight: 600, color: '#1e293b' }}>
                      ${getCardPrice(selectedBin)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography sx={{ color: '#64748b' }}>Initial Balance</Typography>
                    <Typography sx={{ fontWeight: 600, color: '#1e293b' }}>
                      ${formData.initialAmount}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography sx={{ color: '#64748b' }}>Loading Fee</Typography>
                    <Typography sx={{ fontWeight: 600, color: '#1e293b' }}>
                      ${calculateLoadingFee(formData.initialAmount).feeAmount}
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                      Total
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#FFD600' }}>
                      ${(getCardPrice(selectedBin) + calculateLoadingFee(formData.initialAmount).totalAmount).toFixed(2)}
                    </Typography>
                  </Box>
                </Paper>

                {error && (
                  <Alert severity="error" sx={{ mt: 3, borderRadius: 2 }}>
                    {error}
                  </Alert>
                )}
              </DialogContent>

              <DialogActions sx={{ p: 3, pt: 0, gap: 2 }}>
                <Button
                  onClick={() => setOpenDialog(false)}
                  variant="outlined"
                  size="large"
                  sx={{
                    borderRadius: 2,
                    borderColor: '#e2e8f0',
                    color: '#64748b',
                    '&:hover': {
                      borderColor: '#cbd5e1',
                      bgcolor: '#f8fafc',
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleCreateCard}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AddCircleOutline />}
                  sx={{
                    px: 4,
                    borderRadius: 2,
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #FFD600 0%, #FFC400 100%)',
                    boxShadow: '0 4px 12px rgba(255, 214, 0, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #FFAA00 0%, #FF8C00 100%)',
                      boxShadow: '0 6px 16px rgba(255, 214, 0, 0.4)',
                    },
                    '&:disabled': {
                      background: 'linear-gradient(135deg, #94a3b8 0%, #cbd5e1 100%)',
                    }
                  }}
                >
                  {loading ? 'Creating Card...' : 'Create Virtual Card'}
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Container>
    </Box>
  );
}